import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Http from "../../Common/Http";
import {
  ChartCard,
  DeltaBadge,
  FONT,
  StatTile,
  VIZ,
  VizTooltip,
  axisProps,
  compact,
  compactMoney,
  gridProps,
  legendProps,
  money,
  number,
} from "../../Common/Viz";
import AccountsDashboardContent from "./AccountsDashboardContent";

const iso = (date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

// Presets first, custom range behind them - the order readers actually reach for.
const PRESETS = [
  { key: "today", label: "Today", range: () => [iso(new Date()), iso(new Date())] },
  { key: "7d", label: "7 days", range: () => [iso(daysAgo(6)), iso(new Date())] },
  { key: "30d", label: "30 days", range: () => [iso(daysAgo(29)), iso(new Date())] },
  { key: "90d", label: "90 days", range: () => [iso(daysAgo(89)), iso(new Date())] },
  {
    key: "mtd",
    label: "Month to date",
    range: () => {
      const now = new Date();
      return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)];
    },
  },
  {
    key: "ytd",
    label: "Year to date",
    range: () => {
      const now = new Date();
      return [iso(new Date(now.getFullYear(), 0, 1)), iso(now)];
    },
  },
];

const SECTION_LABEL = {
  color: VIZ.inkMuted,
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  margin: "0 0 12px 0",
  fontFamily: FONT,
};

const SeriesKey = ({ color, children, value }) => (
  <div
    className="d-flex justify-content-between align-items-baseline"
    style={{ marginBottom: "2px" }}
  >
    <span className="d-flex align-items-center" style={{ gap: "6px" }}>
      <span
        style={{
          width: "12px",
          height: "2px",
          borderRadius: "1px",
          background: color,
        }}
      />
      <span style={{ color: VIZ.inkSecondary, fontSize: "12px" }}>
        {children}
      </span>
    </span>
    <strong style={{ color: VIZ.ink, fontSize: "12px" }}>{value}</strong>
  </div>
);

/**
 * One panel of a small-multiples stack. Each measure keeps its own y-scale in
 * its own plot rather than being crushed onto a second axis of another chart.
 */
const MiniSeries = ({
  data,
  dataKey,
  name,
  color,
  total,
  format = number,
  height = 104,
  showAxis = false,
}) => (
  <div style={{ marginBottom: showAxis ? 0 : "10px" }}>
    <SeriesKey color={color} value={format(total)}>
      {name}
    </SeriesKey>
    <ResponsiveContainer width="100%" height={showAxis ? height + 22 : height}>
      <AreaChart data={data} margin={{ top: 4, right: 6, left: 0, bottom: 0 }}>
        <CartesianGrid {...gridProps} />
        <XAxis
          dataKey="name"
          {...axisProps}
          hide={!showAxis}
          minTickGap={28}
          height={22}
        />
        <YAxis {...axisProps} width={46} tickFormatter={compact} />
        <Tooltip content={<VizTooltip format={format} />} />
        <Area
          type="monotone"
          dataKey={dataKey}
          name={name}
          stroke={color}
          strokeWidth={2}
          fill={color}
          fillOpacity={0.1}
          dot={false}
          activeDot={{ r: 4, strokeWidth: 2, stroke: VIZ.surface }}
        />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

// Axis labels are truncated rather than wrapped - a wrapped name collides with
// its neighbours. The full name stays in the tooltip and the table view.
const truncate = (value) =>
  String(value).length > 22 ? String(value).slice(0, 21) + "…" : value;

// A ranked horizontal bar - one nominal dimension, so one hue for every bar.
const RankedBars = ({ data, valueKey = "value", format = compactMoney }) => (
  <ResponsiveContainer width="100%" height={340}>
    <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
      <CartesianGrid {...gridProps} vertical horizontal={false} />
      <XAxis type="number" {...axisProps} tickFormatter={compact} />
      <YAxis
        type="category"
        dataKey="name"
        {...axisProps}
        width={140}
        interval={0}
        tickFormatter={truncate}
        tick={{ fill: VIZ.inkSecondary, fontSize: 11 }}
      />
      <Tooltip cursor={{ fill: "rgba(11,11,11,0.04)" }} content={<VizTooltip format={format} />} />
      <Bar
        dataKey={valueKey}
        name="Value"
        fill={VIZ.series[0]}
        maxBarSize={18}
        radius={[0, 4, 4, 0]}
      />
    </BarChart>
  </ResponsiveContainer>
);

const Admin = () => {
  const navigate = useNavigate();
  const defaultRange = useMemo(() => PRESETS[5].range(), []);

  const [fromDate, setFromDate] = useState(defaultRange[0]);
  const [toDate, setToDate] = useState(defaultRange[1]);
  const [appliedRange, setAppliedRange] = useState(defaultRange);
  const [activePreset, setActivePreset] = useState("ytd");
  const [data, setData] = useState(null);
  const [accountsData, setAccountsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const loadedOnce = useRef(false);

  const fetchDashboard = useCallback((range) => {
    // First load gets the spinner; later loads hold the previous render at
    // reduced opacity so the layout never jumps.
    if (loadedOnce.current) setRefreshing(true);
    else setLoading(true);
    setError("");

    // The admin view shows the operations figures plus the full accounts
    // dashboard, so both endpoints are pulled together.
    Promise.all([
      Http.get("/admin/dashboard", {
        params: { from_date: range[0], to_date: range[1] },
      }),
      Http.get("/accounts/dashboard", {
        params: { start_date: range[0], end_date: range[1] },
      }),
    ])
      .then(([operations, accounts]) => {
        setData(operations.data);
        setAccountsData(accounts.data);
      })
      .catch((e) => {
        console.error("Dashboard fetch error:", e);
        setError(
          e?.response?.data?.message ||
            "Could not load the dashboard. Please try again."
        );
      })
      .finally(() => {
        loadedOnce.current = true;
        setLoading(false);
        setRefreshing(false);
      });
  }, []);

  useEffect(() => {
    fetchDashboard(appliedRange);
  }, [appliedRange, fetchDashboard]);

  const applyPreset = (preset) => {
    const range = preset.range();
    setFromDate(range[0]);
    setToDate(range[1]);
    setActivePreset(preset.key);
    setAppliedRange(range);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setActivePreset("");
    setAppliedRange([fromDate, toDate]);
  };

  const summary = data?.summary || {};
  const deltas = data?.deltas || {};
  const cards = data?.cards || {};
  const monthlyTrend = data?.monthly_trend || [];
  const productionTrend = data?.production_trend || [];
  const dailyPurchase = data?.daily_purchase || [];
  const categoryStock = data?.category_stock || [];
  const topProducts = data?.top_products || [];
  const topVendors = data?.top_vendors || [];
  const stockAlerts = data?.stock_alerts || [];
  const recentActivities = data?.recent_activities || [];
  const prevNote = deltas.previous_label ? `vs ${deltas.previous_label}` : "";

  const sum = (rows, key) => rows.reduce((total, row) => total + (row[key] || 0), 0);
  const dailyHasActivity = dailyPurchase.some((row) => row.purchase || row.issue);

  if (loading) {
    return (
      <div className="am-mainpanel">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "60vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="am-mainpanel" style={{ fontFamily: FONT,marginTop:"60px" }}>
      {/* Page header + the single filter row that scopes everything below it */}
      <div
        style={{
          // background: VIZ.surface,
          background: "whitesmoke",
          borderBottom: `1px solid ${VIZ.border}`,
          padding: "18px 20px 14px"
        }}
      >
        <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: "12px" }}>
          <div>
            <h5
              style={{
                color: VIZ.ink,
                fontSize: "18px",
                fontWeight: 600,
                margin: 0,
              }}
            >
              Admin dashboard
            </h5>
            <p style={{ color: VIZ.inkMuted, fontSize: "12px", margin: "3px 0 0 0" }}>
              {data?.range?.label}
              {refreshing ? " · updating…" : ""}
            </p>
          </div>

          <form
            className="d-flex align-items-center flex-wrap"
            style={{ gap: "8px" }}
            onSubmit={handleSubmit}
          >
            <div
              className="d-flex"
              style={{
                border: `1px solid ${VIZ.border}`,
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  aria-pressed={activePreset === preset.key}
                  style={{
                    border: "none",
                    background:
                      activePreset === preset.key ? VIZ.series[0] : VIZ.surface,
                    color: activePreset === preset.key ? "#fff" : VIZ.inkSecondary,
                    fontSize: "12px",
                    fontWeight: activePreset === preset.key ? 600 : 400,
                    padding: "7px 12px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <input
              type="date"
              aria-label="From date"
              className="form-control"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{ width: "145px", fontSize: "12px" }}
            />
            <input
              type="date"
              aria-label="To date"
              className="form-control"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{ width: "145px", fontSize: "12px" }}
            />
            <button
              type="submit"
              className="btn"
              style={{
                background: VIZ.series[0],
                color: "#fff",
                fontSize: "12px",
                padding: "7px 16px",
                borderRadius: "8px",
              }}
            >
              Apply
            </button>
          </form>
        </div>
      </div>

      <div className="am-pagebody" style={{ padding: "20px" }}>
        {error ? (
          <div className="alert alert-danger d-flex justify-content-between align-items-center">
            <span>{error}</span>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => fetchDashboard(appliedRange)}
            >
              Retry
            </button>
          </div>
        ) : null}


        {/* Accounts */}
        <p style={{ ...SECTION_LABEL, marginTop: "24px" }}>
          Accounts — totals and monthly charts follow the range above; bank
          balances are cumulative
        </p>
        <AccountsDashboardContent data={accountsData} stale={refreshing} />

        {/* Hero: the one number the dashboard leads with */}
        <div className="row row-sm" style={{marginTop:"20px"}}>
          <div className="col-lg-5">
            <div
              style={{
                background: VIZ.surface,
                border: `1px solid ${VIZ.border}`,
                borderRadius: "10px",
                padding: "20px 22px",
                height: "100%",
                opacity: refreshing ? 0.45 : 1,
                transition: "opacity 0.2s ease",
              }}
            >
              <p style={{ color: VIZ.inkSecondary, fontSize: "12px", margin: 0 }}>
                Total purchase
              </p>
              <div
                style={{
                  color: VIZ.ink,
                  fontSize: "48px",
                  fontWeight: 600,
                  lineHeight: 1.05,
                  margin: "4px 0 6px",
                }}
              >
                {money(summary.purchase_amount)}
              </div>
              <div className="d-flex align-items-center flex-wrap" style={{ gap: "10px" }}>
                <DeltaBadge change={deltas.purchase?.change} note={prevNote} />
                <span style={{ color: VIZ.inkMuted, fontSize: "11px" }}>
                  {number(summary.purchase_count)} receipts
                </span>
              </div>

              <div style={{ marginTop: "18px" }}>
                <ResponsiveContainer width="100%" height={110}>
                  <AreaChart data={monthlyTrend} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                    <Tooltip content={<VizTooltip format={money} />} />
                    <Area
                      type="monotone"
                      dataKey="purchase"
                      name="Purchase"
                      stroke={VIZ.series[0]}
                      strokeWidth={2}
                      fill={VIZ.series[0]}
                      fillOpacity={0.1}
                      dot={false}
                      activeDot={{ r: 4, strokeWidth: 2, stroke: VIZ.surface }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                <p style={{ color: VIZ.inkMuted, fontSize: "11px", margin: 0 }}>
                  Purchase by month across the range
                </p>
              </div>
            </div>
          </div>

          {/* Current-period figures beside the hero */}
          <div className="col-lg-7 mg-t-15 mg-lg-t-0">
            <div className="row row-sm" style={{ height: "100%" }}>
              {["today", "week", "month"].map((key, index) => {
                const card = cards[key] || {};
                return (
                  <div key={key} className={index ? "col-md-4 mg-t-15 mg-md-t-0" : "col-md-4"}>
                    <div
                      style={{
                        background: VIZ.surface,
                        border: `1px solid ${VIZ.border}`,
                        borderRadius: "10px",
                        padding: "16px 18px",
                        height: "100%",
                        opacity: refreshing ? 0.45 : 1,
                        transition: "opacity 0.2s ease",
                      }}
                    >
                      <p style={{ color: VIZ.inkSecondary, fontSize: "12px", margin: 0 }}>
                        {card.title}
                      </p>
                      <p style={{ color: VIZ.inkMuted, fontSize: "11px", margin: "1px 0 6px" }}>
                        {card.label}
                      </p>
                      <div
                        style={{
                          color: VIZ.ink,
                          fontSize: "22px",
                          fontWeight: 600,
                          lineHeight: 1.15,
                        }}
                      >
                        {money(card.purchase)}
                      </div>
                      <p style={{ color: VIZ.inkMuted, fontSize: "11px", margin: "2px 0 8px" }}>
                        {number(card.purchase_count)} receipts ·{" "}
                        {number(card.issue_count)} issues
                      </p>
                      {/* A flat line of zeroes reads as a broken chart - say so instead. */}
                      {card.purchase ? (
                        <ResponsiveContainer width="100%" height={70}>
                          <AreaChart
                            data={card.trend || []}
                            margin={{ top: 2, right: 0, left: 0, bottom: 0 }}
                          >
                            <Tooltip content={<VizTooltip format={money} />} />
                            <Area
                              type="monotone"
                              dataKey="value"
                              name="Purchase"
                              stroke={VIZ.series[0]}
                              strokeWidth={2}
                              fill={VIZ.series[0]}
                              fillOpacity={0.1}
                              dot={false}
                              activeDot={{ r: 4, strokeWidth: 2, stroke: VIZ.surface }}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <div
                          className="d-flex align-items-center"
                          style={{ height: "70px", color: VIZ.inkMuted, fontSize: "11px" }}
                        >
                          No purchases recorded in this period.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* KPI row */}
        <p style={{ ...SECTION_LABEL, marginTop: "24px" }}>At a glance</p>
        <div className="row row-sm" style={{ opacity: refreshing ? 0.45 : 1 }}>
          {[
            {
              label: "Stock value",
              value: money(summary.stock_value),
              sub: `${number(summary.stock_items)} items`,
            },
            {
              label: "Stock alerts",
              value: number(summary.low_stock_count),
              sub: `${number(summary.out_of_stock_count)} out of stock`,
              accent: VIZ.status.critical,
            },
            {
              label: "Production quantity",
              value: number(summary.production_qty),
              sub: `${number(summary.production_count)} batches`,
              change: deltas.production_qty?.change,
            },
            {
              label: "Pending requisitions",
              value: number(summary.pending_requisitions),
              sub: `${number(summary.requisition_count)} raised in range`,
              accent: VIZ.status.warning,
            },
            {
              label: "Store issues",
              value: number(summary.issue_count),
              sub: `${number(summary.damage_count)} damage entries`,
              change: deltas.issue_count?.change,
            },
            {
              label: "Expense",
              value: money(summary.expense),
              sub: "in range",
              change: deltas.expense?.change,
              goodWhenUp: false,
            },
            {
              label: "Deposit",
              value: money(summary.deposit),
              sub: `net ${money(summary.net_cash)}`,
              change: deltas.deposit?.change,
            },
            // {
            //   label: "Present today",
            //   value: `${number(summary.attendance_today)} / ${number(summary.active_staff)}`,
            //   sub: `${number(summary.total_vendors)} active vendors`,
            // },
          ].map((tile) => (
            <div key={tile.label} className="col-xl-3 col-lg-4 col-md-6 mg-t-15">
              <StatTile {...tile} />
            </div>
          ))}
        </div>

        {/* Flows */}
        <p style={{ ...SECTION_LABEL, marginTop: "24px" }}>Flows</p>
        <div className="row row-sm">
          <div className="col-lg-6">
            <ChartCard
              title="Purchase, expense and deposit by month"
              note="Three measures on one scale, compared month by month."
              stale={refreshing}
              rows={monthlyTrend}
              columns={[
                { key: "name", label: "Month" },
                { key: "purchase", label: "Purchase", align: "right", format: money },
                { key: "expense", label: "Expense", align: "right", format: money },
                { key: "deposit", label: "Deposit", align: "right", format: money },
              ]}
            >
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={monthlyTrend} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="name" {...axisProps} />
                  <YAxis {...axisProps} width={54} tickFormatter={compact} />
                  <Tooltip
                    cursor={{ fill: "rgba(11,11,11,0.04)" }}
                    content={<VizTooltip format={money} />}
                  />
                  <Legend {...legendProps} />
                  <Bar dataKey="purchase" name="Purchase" fill={VIZ.series[0]} maxBarSize={24} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expense" name="Expense" fill={VIZ.series[1]} maxBarSize={24} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="deposit" name="Deposit" fill={VIZ.series[2]} maxBarSize={24} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <div className="col-lg-6 mg-t-15 mg-lg-t-0">
            <ChartCard
              title="Requisition, issue and production volume"
              note="Separate plots — these three counts live on very different scales."
              stale={refreshing}
              rows={productionTrend}
              columns={[
                { key: "name", label: "Month" },
                { key: "requisition", label: "Requisitions", align: "right", format: number },
                { key: "issue", label: "Issues", align: "right", format: number },
                { key: "production", label: "Productions", align: "right", format: number },
              ]}
            >
              <div>
                <MiniSeries
                  data={productionTrend}
                  dataKey="requisition"
                  name="Requisitions"
                  color={VIZ.series[0]}
                  total={sum(productionTrend, "requisition")}
                  height={88}
                />
                <MiniSeries
                  data={productionTrend}
                  dataKey="issue"
                  name="Issues"
                  color={VIZ.series[1]}
                  total={sum(productionTrend, "issue")}
                  height={88}
                />
                <MiniSeries
                  data={productionTrend}
                  dataKey="production"
                  name="Productions"
                  color={VIZ.series[2]}
                  total={sum(productionTrend, "production")}
                  height={88}
                  showAxis
                />
              </div>
            </ChartCard>
          </div>
        </div>

        <div className="row row-sm mg-t-15">
          <div className="col-12">
            <ChartCard
              title="Daily movement"
              note="Purchase value and issue count, each on its own scale."
              stale={refreshing}
              rows={dailyHasActivity ? dailyPurchase : []}
              empty="No purchases or issues in the last 60 days of this range."
              columns={[
                { key: "date", label: "Date" },
                { key: "purchase", label: "Purchase", align: "right", format: money },
                { key: "issue", label: "Issues", align: "right", format: number },
              ]}
            >
              <div className="row row-sm">
                <div className="col-md-6">
                  <MiniSeries
                    data={dailyPurchase}
                    dataKey="purchase"
                    name="Purchase value"
                    color={VIZ.series[0]}
                    total={sum(dailyPurchase, "purchase")}
                    format={money}
                    height={150}
                    showAxis
                  />
                </div>
                <div className="col-md-6 mg-t-15 mg-md-t-0">
                  <MiniSeries
                    data={dailyPurchase}
                    dataKey="issue"
                    name="Store issues"
                    color={VIZ.series[1]}
                    total={sum(dailyPurchase, "issue")}
                    height={150}
                    showAxis
                  />
                </div>
              </div>
            </ChartCard>
          </div>
        </div>

        {/* Rankings */}
        <p style={{ ...SECTION_LABEL, marginTop: "24px" }}>Where the money sits</p>
        <div className="row row-sm">
          <div className="col-lg-4">
            <ChartCard
              title="Stock value by category"
              note="Current holding, not scoped to the range."
              stale={refreshing}
              rows={categoryStock}
              columns={[
                { key: "name", label: "Category" },
                { key: "items", label: "Items", align: "right", format: number },
                { key: "value", label: "Value", align: "right", format: money },
              ]}
            >
              <RankedBars data={categoryStock} />
            </ChartCard>
          </div>

          <div className="col-lg-4 mg-t-15 mg-lg-t-0">
            <ChartCard
              title="Top purchased products"
              note="By purchase value in the selected range."
              stale={refreshing}
              rows={topProducts}
              columns={[
                { key: "code", label: "Code" },
                { key: "name", label: "Product" },
                { key: "qty", label: "Qty", align: "right", format: number },
                { key: "value", label: "Value", align: "right", format: money },
              ]}
            >
              <RankedBars data={topProducts} />
            </ChartCard>
          </div>

          <div className="col-lg-4 mg-t-15 mg-lg-t-0">
            <ChartCard
              title="Top vendors"
              note="By purchase value in the selected range."
              stale={refreshing}
              rows={topVendors}
              columns={[
                { key: "name", label: "Vendor" },
                { key: "receipts", label: "Receipts", align: "right", format: number },
                { key: "value", label: "Value", align: "right", format: money },
              ]}
            >
              <RankedBars data={topVendors} />
            </ChartCard>
          </div>
        </div>

        {/* Lists */}
        <p style={{ ...SECTION_LABEL, marginTop: "24px" }}>Needs attention</p>
        <div className="row row-sm">
          <div className="col-lg-6">
            <div
              style={{
                background: VIZ.surface,
                border: `1px solid ${VIZ.border}`,
                borderRadius: "10px",
                padding: "18px",
                height: "100%",
              }}
            >
              <h6 style={{ color: VIZ.ink, fontSize: "14px", fontWeight: 600, margin: "0 0 12px" }}>
                Lowest stock items
              </h6>
              <div className="table-responsive">
                <table className="table table-sm mg-b-0" style={{ fontSize: "12px" }}>
                  <thead>
                    <tr>
                      <th style={{ color: VIZ.inkSecondary }}>Code</th>
                      <th style={{ color: VIZ.inkSecondary }}>Product</th>
                      <th className="tx-right" style={{ color: VIZ.inkSecondary }}>Stock</th>
                      <th className="tx-right" style={{ color: VIZ.inkSecondary }}>Value</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontVariantNumeric: "tabular-nums" }}>
                    {stockAlerts.length ? (
                      stockAlerts.map((item) => {
                        const out = item.stock_qty <= 0;
                        return (
                          <tr
                            key={item.id}
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              navigate(
                                `/product-ledger/:id?productId=${item.id}&productCode=${item.code}&productName=${encodeURIComponent(item.name)}`
                              )
                            }
                          >
                            <td style={{ color: VIZ.inkMuted }}>{item.code}</td>
                            <td style={{ color: VIZ.ink }}>
                              {item.name}
                              <span style={{ color: VIZ.inkMuted, fontSize: "11px", display: "block" }}>
                                {item.category}
                              </span>
                            </td>
                            <td className="tx-right">
                              <span
                                className="d-inline-flex align-items-center"
                                style={{
                                  gap: "4px",
                                  color: out ? VIZ.status.critical : VIZ.inkSecondary,
                                  fontWeight: 600,
                                }}
                              >
                                <i
                                  className={`fa fa-${out ? "times-circle" : "exclamation-triangle"}`}
                                  aria-hidden="true"
                                />
                                {number(item.stock_qty)} {item.unit}
                              </span>
                              <span style={{ color: VIZ.inkMuted, fontSize: "11px", display: "block" }}>
                                {out ? "Out of stock" : "Low"}
                              </span>
                            </td>
                            <td className="tx-right" style={{ color: VIZ.ink }}>
                              {money(item.value)}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" className="tx-center" style={{ color: VIZ.inkMuted }}>
                          Nothing to flag.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mg-t-15 mg-lg-t-0">
            <div
              style={{
                background: VIZ.surface,
                border: `1px solid ${VIZ.border}`,
                borderRadius: "10px",
                padding: "18px",
                height: "100%",
              }}
            >
              <h6 style={{ color: VIZ.ink, fontSize: "14px", fontWeight: 600, margin: "0 0 12px" }}>
                Recent activity
              </h6>
              <div className="table-responsive">
                <table className="table table-sm mg-b-0" style={{ fontSize: "12px" }}>
                  <thead>
                    <tr>
                      <th style={{ color: VIZ.inkSecondary }}>Type</th>
                      <th style={{ color: VIZ.inkSecondary }}>Reference</th>
                      <th style={{ color: VIZ.inkSecondary }}>Details</th>
                      <th className="tx-right" style={{ color: VIZ.inkSecondary }}>Date</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontVariantNumeric: "tabular-nums" }}>
                    {recentActivities.length ? (
                      recentActivities.map((item, index) => (
                        <tr key={`${item.type}-${item.reference}-${index}`}>
                          <td style={{ color: VIZ.inkSecondary }}>{item.type}</td>
                          <td style={{ color: VIZ.inkMuted }}>{item.reference || "-"}</td>
                          <td style={{ color: VIZ.ink }}>
                            {item.party}
                            {item.amount != null ? (
                              <span style={{ color: VIZ.inkMuted, fontSize: "11px", display: "block" }}>
                                {money(item.amount)}
                              </span>
                            ) : null}
                            {item.qty != null ? (
                              <span style={{ color: VIZ.inkMuted, fontSize: "11px", display: "block" }}>
                                Qty {number(item.qty)}
                              </span>
                            ) : null}
                          </td>
                          <td className="tx-right" style={{ color: VIZ.inkSecondary }}>
                            {item.date}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="tx-center" style={{ color: VIZ.inkMuted }}>
                          No recent activity.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="am-footer">
        <span>
          Copyright ©. All Rights Reserved.{" "}
          <a href="http://limerickbd.com/">Limerick Resources Limited</a>
        </span>
      </div>
    </div>
  );
};

export default Admin;
